
import { useState, useEffect } from "react"
import { FileText, Check, X, Printer, Download, Eye, Clock, Loader2, Edit, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { Layout } from "@/components/Layout"
import { useProfiles } from "@/hooks/useProfiles"
import { useEmployeeTimesheetReport, useTimesheetRecords, useDeleteTimesheetRecord } from "@/hooks/useTimesheetRecords"
import { useUpdateTimesheetApproval } from "@/hooks/useTimesheets"
import { useAuth } from "@/hooks/useAuth"
import { useCondominium } from "@/hooks/useCondominium"
import { useVersioning } from "@/hooks/useVersioning"
import { EditTimesheetRecordModal } from "@/components/EditTimesheetRecordModal"
import { EditDayTimesheetModal } from "@/components/EditDayTimesheetModal"
import { OperadorTimesheetView } from "@/components/timesheet/OperadorTimesheetView"

const FolhasPonto = () => {
  const { profile } = useAuth()
  const { data: condominium } = useCondominium()

  // SEGURANÇA: Log de auditoria para tentativas de acesso de porteiros
  useEffect(() => {
    if (profile?.role === 'porteiro') {
      console.warn('⚠️ AUDITORIA: Porteiro tentou acessar página de gestão de folhas de ponto:', {
        userId: profile.id,
        userEmail: profile.email,
        userName: profile.full_name,
        condominiumId: profile.condominium_id,
        timestamp: new Date().toISOString()
      })
    }
  }, [profile])
  
  // SEGURANÇA: Porteiros NUNCA devem acessar a página de gestão de folhas de ponto
  // Eles só podem ver seus próprios registros através do OperadorTimesheetView
  if (profile?.role === 'porteiro') {
    return (
      <Layout>
        <div className="p-6">
          <OperadorTimesheetView />
        </div>
      </Layout>
    )
  }
  const { data: profiles, isLoading: loadingProfiles } = useProfiles()
  const { incrementVersion } = useVersioning()
  const [justificativaModal, setJustificativaModal] = useState(false)
  const [folhaSelecionada, setFolhaSelecionada] = useState<any>(null)
  const [acao, setAcao] = useState("")
  const [justificativa, setJustificativa] = useState("")
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("")
  const [visualizarModal, setVisualizarModal] = useState(false)
  const [editRecordModal, setEditRecordModal] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<any>(null)
  const [selectedDay, setSelectedDay] = useState<{date: string, records: any[]}>()
  const [isEditDayModalOpen, setIsEditDayModalOpen] = useState(false)
  const [shouldFetchReport, setShouldFetchReport] = useState(false)
  
  const updateApproval = useUpdateTimesheetApproval()
  const deleteRecord = useDeleteTimesheetRecord()
  
  // Buscar registros do funcionário selecionado para mostrar detalhes editáveis
  const { data: timesheetRecords } = useTimesheetRecords(selectedEmployeeId)
  
  // Verificar se é condomínio de ponto eletrônico
  const isPontoEletronicoCondominium = condominium?.type === "ponto_eletronico"
  
  // Verificar se o usuário pode aprovar diretamente (administração em condomínio ponto eletrônico)
  const canApproveDirectly = profile?.role === "administracao" && isPontoEletronicoCondominium
  
  // Verificar se pode editar manualmente (administração em condomínio ponto eletrônico)
  const canEditManually = profile?.role === "administracao" && isPontoEletronicoCondominium
  
  // Filtrar apenas funcionários (porteiros)
  const funcionarios = profiles?.filter(p => p.role === 'porteiro') || []

  // Estados para filtros de data
  const [selectedStartDate, setSelectedStartDate] = useState<string>("")
  const [selectedEndDate, setSelectedEndDate] = useState<string>("")
  
  // Calcular datas do mês atual
  const currentDate = new Date()
  const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
  const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)

  // Helper para formatar data local YYYY-MM-DD (evita offset/ISO)
  const toLocalYMD = (d: Date) => {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }

  // Parser local para evitar timezone issues
  const formatYMDToBR = (ymd: string) => {
    if (!ymd) return 'Data inválida'
    const [y, m, d] = ymd.split('-').map(Number)
    return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`
  }
  
  // Usar datas selecionadas ou padrão do mês atual (local)
  const startDate = selectedStartDate || toLocalYMD(startOfMonth)
  const endDate = selectedEndDate || toLocalYMD(endOfMonth)

  const { data: relatorioData, isLoading: loadingReport } = useEmployeeTimesheetReport(
    selectedEmployeeId, 
    shouldFetchReport ? startDate : "", 
    shouldFetchReport ? endDate : ""
  )
  
  // Funções para botões de atalho de períodos
  const setCurrentMonth = () => {
    const now = new Date()
    const start = new Date(now.getFullYear(), now.getMonth(), 1)
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    setSelectedStartDate(toLocalYMD(start))
    setSelectedEndDate(toLocalYMD(end))
  }
  
  const setLastMonth = () => {
    const now = new Date()
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const end = new Date(now.getFullYear(), now.getMonth(), 0)
    setSelectedStartDate(toLocalYMD(start))
    setSelectedEndDate(toLocalYMD(end))
  }

  const formatTime = (date: string | Date) => {
    return new Date(date).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatInterval = (interval: string | null) => {
    if (!interval) return "0h 0m"
    
    const match = interval.match(/(\d+):(\d+):(\d+)/)
    if (match) {
      const hours = parseInt(match[1])
      const minutes = parseInt(match[2])
      return `${hours}h ${minutes}m`
    }
    return interval
  }

  const calcularTotalHoras = (dados: any[]) => {
    let totalMinutos = 0
    
    dados.forEach(dia => {
      if (dia.total_horas) {
        const match = dia.total_horas.match(/(\d+):(\d+):(\d+)/)
        if (match) {
          const horas = parseInt(match[1])
          const minutos = parseInt(match[2])
          totalMinutos += (horas * 60) + minutos
        }
      }
    })
    
    const totalHoras = Math.floor(totalMinutos / 60)
    const minutosRestantes = totalMinutos % 60
    return `${totalHoras}h ${minutosRestantes}m`
  }

  const gerarFolhasPonto = (startDateParam: string, endDateParam: string) => {
    const [y, m] = startDateParam.split('-')
    const mesFormatado = `${m}/${y}`
    const dataEnvio = formatYMDToBR(endDateParam)
    
    return funcionarios.map(funcionario => ({
      id: funcionario.id,
      funcionario: funcionario.full_name || "Nome não informado",
      cargo: "Porteiro",
      mes: mesFormatado,
      totalHoras: "Carregando...",
      status: "aguardando_sindico",
      dataEnvio: dataEnvio,
      employeeId: funcionario.id
    }))
  }

  const folhasPonto = gerarFolhasPonto(startDate, endDate)

  const getStatusBadge = (status: string) => {
    const isPontoEletronico = condominium?.type === 'ponto_eletronico'
    
    switch (status) {
      case "aguardando_sindico":
        if (isPontoEletronico) {
          return <Badge variant="outline" className="text-yellow-600">Aguardando Gerente</Badge>
        }
        return <Badge variant="outline" className="text-yellow-600">Aguardando Síndico</Badge>
      case "aprovado_sindico":
        return <Badge variant="outline" className="text-blue-600">Aprovado pelo Síndico</Badge>
      case "reprovado_sindico":
        return <Badge variant="destructive">Reprovado pelo Síndico</Badge>
      case "aguardando_admin":
        return <Badge variant="outline" className="text-orange-600">Aguardando Administração</Badge>
      case "aprovado_final":
        return <Badge variant="default" className="text-green-600">Aprovado Final</Badge>
      case "reprovado_admin":
        return <Badge variant="destructive">Reprovado pela Administração</Badge>
      case "aprovado_admin_ponto":
        return <Badge variant="default" className="text-green-600">Aprovado - Ponto Eletrônico</Badge>
      case "reprovado_admin_ponto":
        return <Badge variant="destructive">Reprovado - Ponto Eletrônico</Badge>
      default:
        return <Badge variant="outline">Pendente</Badge>
    }
  }

  const handleAcao = (folha: any, tipoAcao: string) => {
    setFolhaSelecionada(folha)
    setAcao(tipoAcao)
    setJustificativaModal(true)
  }

  const confirmarAcao = async () => {
    if (!folhaSelecionada) return

    try {
      let status = ""
      
      if (canApproveDirectly) {
        // Para administração em condomínio ponto eletrônico
        switch (acao) {
          case "aprovar":
            status = "aprovado_admin_ponto"
            break
          case "reprovar":
            status = "reprovado_admin_ponto"
            break
          default:
            status = acao === "aprovar_final" ? "aprovado_final" : acao
        }
      } else {
        // Fluxo tradicional
        switch (acao) {
          case "aprovar":
            status = "aprovado_sindico"
            break
          case "reprovar":
            status = "reprovado_sindico"
            break
          case "aprovar_final":
            status = "aprovado_final"
            break
          case "reprovar_admin":
            status = "reprovado_admin"
            break
          default:
            status = acao
        }
      }

      await updateApproval.mutateAsync({
        timesheetId: folhaSelecionada.id,
        status,
        justificativa
      })

      setJustificativaModal(false)
      setJustificativa("")
      setFolhaSelecionada(null)
    } catch (error) {
      console.error("Erro ao confirmar ação:", error)
    }
  }

  const visualizarDetalhes = (employeeId: string, folha: any) => {
    console.log("Visualizando detalhes para funcionário:", employeeId)
    setSelectedEmployeeId(employeeId)
    setFolhaSelecionada(folha)
    setShouldFetchReport(true)
    setVisualizarModal(true)
  }

  const handleEditRecord = (record: any) => {
    setSelectedRecord(record)
    setEditRecordModal(true)
  }

  const handleDeleteRecord = async (recordId: string) => {
    if (window.confirm("Tem certeza que deseja excluir este registro?")) {
      try {
        await deleteRecord.mutateAsync(recordId)
      } catch (error) {
        console.error("Erro ao excluir registro:", error)
      }
    }
  }

  const handleEditDay = (date: string) => {
    // Preferir registros reais do dia; se não houver, montar a partir do relatório
    let dayRecords = timesheetRecords?.filter(r => r.record_date === date) || []

    if ((!dayRecords || dayRecords.length === 0) && relatorioData) {
      const resumo = relatorioData.find((d) => d.date === date)
      if (resumo) {
        dayRecords = [
          resumo.entrada ? { record_type: 'entrada', record_time: resumo.entrada, location: '', notes: resumo.observacoes || '' } : null,
          resumo.saida_almoco ? { record_type: 'saida_almoco', record_time: resumo.saida_almoco, location: '', notes: resumo.observacoes || '' } : null,
          resumo.volta_almoco ? { record_type: 'volta_almoco', record_time: resumo.volta_almoco, location: '', notes: resumo.observacoes || '' } : null,
          resumo.saida ? { record_type: 'saida', record_time: resumo.saida, location: '', notes: resumo.observacoes || '' } : null,
        ].filter(Boolean) as any[]
      }
    }

    setSelectedDay({ date, records: dayRecords })
    setIsEditDayModalOpen(true)
  }

  const imprimirFolha = (folha: any) => {
    // Buscar dados atualizados para impressão
    if (selectedEmployeeId === folha.employeeId && relatorioData) {
      const printWindow = window.open('', '_blank')
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Folha de Ponto - ${folha.funcionario}</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                .header { text-align: center; margin-bottom: 30px; }
                .info { margin-bottom: 20px; }
                table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
                th, td { border: 1px solid #000; padding: 8px; text-align: center; }
                th { background-color: #f0f0f0; }
                .assinatura { margin-top: 50px; }
                .linha { border-bottom: 1px solid #000; margin-top: 30px; }
                @media print { 
                  body { margin: 0; }
                  .no-print { display: none; }
                }
              </style>
            </head>
            <body>
              <div class="header">
                <h2>FOLHA DE PONTO INDIVIDUAL</h2>
                <p>Condomínio Residencial</p>
              </div>
              
              <div class="info">
                <p><strong>Funcionário:</strong> ${folha.funcionario}</p>
                <p><strong>Cargo:</strong> ${folha.cargo}</p>
                <p><strong>Mês/Ano:</strong> ${folha.mes}</p>
                <p><strong>Total de Horas:</strong> ${calcularTotalHoras(relatorioData)}</p>
              </div>
              
              <table>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Entrada</th>
                    <th>Saída Almoço</th>
                    <th>Volta Almoço</th>
                    <th>Saída</th>
                    <th>Total Horas</th>
                    <th>Observações</th>
                  </tr>
                </thead>
                <tbody>
                  ${relatorioData.map(dia => `
                    <tr>
                      <td>${formatYMDToBR(dia.date)}</td>
                      <td>${dia.entrada ? formatTime(dia.entrada) : '-'}</td>
                      <td>${dia.saida_almoco ? formatTime(dia.saida_almoco) : '-'}</td>
                      <td>${dia.volta_almoco ? formatTime(dia.volta_almoco) : '-'}</td>
                      <td>${dia.saida ? formatTime(dia.saida) : '-'}</td>
                      <td>${formatInterval(dia.total_horas)}</td>
                      <td>${dia.observacoes || ''}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
              
              <div class="assinatura">
                <div style="display: flex; justify-content: space-between; margin-top: 50px;">
                  <div style="width: 200px;">
                    <div class="linha"></div>
                    <p style="text-align: center;">Funcionário</p>
                  </div>
                  <div style="width: 200px;">
                    <div class="linha"></div>
                    <p style="text-align: center;">Síndico</p>
                  </div>
                  <div style="width: 200px;">
                    <div class="linha"></div>
                    <p style="text-align: center;">Administradora</p>
                  </div>
                </div>
              </div>
              
              <div style="margin-top: 30px; font-size: 12px;">
                <p><strong>Observações:</strong></p>
                <p>Esta folha de ponto está em conformidade com a legislação trabalhista vigente.</p>
                <p>Data de impressão: ${new Date().toLocaleDateString('pt-BR')}</p>
              </div>
              
              <button class="no-print" onclick="window.print()" style="margin-top: 20px; padding: 10px 20px; background: #007bff; color: white; border: none; cursor: pointer;">Imprimir</button>
            </body>
          </html>
        `)
        printWindow.document.close()
      }
    }
  }

  if (loadingProfiles) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Folhas de Ponto</h1>
            <p className="text-gray-600">Gestão e aprovação de folhas de ponto</p>
          </div>
        </div>

        {/* Filtros de Data */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filtros de Período</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Data Início</label>
                <Input
                  type="date"
                  value={selectedStartDate}
                  onChange={(e) => setSelectedStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Data Fim</label>
                <Input
                  type="date"
                  value={selectedEndDate}
                  onChange={(e) => setSelectedEndDate(e.target.value)}
                />
              </div>
              <div className="flex items-end gap-2">
                <Button variant="outline" onClick={setCurrentMonth} size="sm">
                  Mês Atual
                </Button>
              </div>
              <div className="flex items-end gap-2">
                <Button variant="outline" onClick={setLastMonth} size="sm">
                  Mês Anterior
                </Button>
              </div>
              <div className="flex items-end">
                <Button onClick={() => setShouldFetchReport(true)} className="w-full">
                  Buscar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {funcionarios.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Nenhum funcionário encontrado</p>
            <p className="text-sm text-gray-400 mt-2">
              Cadastre funcionários com o perfil "Porteiro" para visualizar suas folhas de ponto
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {folhasPonto.map((folha) => (
              <Card key={folha.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">{folha.funcionario} - {folha.cargo}</CardTitle>
                      <p className="text-sm text-gray-600">{folha.mes} • Enviado em {folha.dataEnvio}</p>
                    </div>
                    {getStatusBadge(folha.status)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => visualizarDetalhes(folha.employeeId, folha)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Visualizar
                    </Button>

                    {/* Botões para síndicos - fluxo tradicional */}
                    {(profile?.role === "sindico" && folha.status === "aguardando_sindico" && !canApproveDirectly) && (
                      <>
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleAcao(folha, "aprovar")}
                        >
                          <Check className="h-4 w-4 mr-2" />
                          Aprovar
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleAcao(folha, "reprovar")}
                        >
                          <X className="h-4 w-4 mr-2" />
                          Reprovar
                        </Button>
                      </>
                    )}

                    {/* Botões para administração em condomínio ponto eletrônico - aprovação direta */}
                    {(canApproveDirectly && ["aguardando_sindico", "reprovado_sindico"].includes(folha.status)) && (
                      <>
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleAcao(folha, "aprovar")}
                        >
                          <Check className="h-4 w-4 mr-2" />
                          Aprovar
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleAcao(folha, "reprovar")}
                        >
                          <X className="h-4 w-4 mr-2" />
                          Reprovar
                        </Button>
                      </>
                    )}

                    {/* Botões para administração - fluxo tradicional */}
                    {(profile?.role === "administracao" && folha.status === "aprovado_sindico" && !canApproveDirectly) && (
                      <>
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleAcao(folha, "aprovar_final")}
                        >
                          <Check className="h-4 w-4 mr-2" />
                          Aprovar Final
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleAcao(folha, "reprovar_admin")}
                        >
                          <X className="h-4 w-4 mr-2" />
                          Reprovar
                        </Button>
                      </>
                    )}

                    {/* Botão de impressão */}
                    {(profile?.role === "administracao" && 
                      (folha.status === "aprovado_final" || folha.status === "aprovado_admin_ponto")) && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => imprimirFolha(folha)}
                      >
                        <Printer className="h-4 w-4 mr-2" />
                        Imprimir
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Modal de Visualização */}
        <Dialog open={visualizarModal} onOpenChange={setVisualizarModal}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>
                Folha de Ponto - {folhaSelecionada?.funcionario} ({folhaSelecionada?.mes})
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {loadingReport ? (
                <div className="flex justify-center p-4">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span className="ml-2">Carregando dados...</span>
                </div>
              ) : relatorioData && relatorioData.length > 0 ? (
                <>
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-lg mb-2">Resumo do Período</h3>
                    <p><strong>Total de Horas Trabalhadas:</strong> {calcularTotalHoras(relatorioData)}</p>
                    <p><strong>Período:</strong> {formatYMDToBR(startDate)} – {formatYMDToBR(endDate)}</p>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Entrada</TableHead>
                        <TableHead>Saída Almoço</TableHead>
                        <TableHead>Volta Almoço</TableHead>
                        <TableHead>Saída</TableHead>
                         <TableHead>Total Horas</TableHead>
                         {canEditManually && <TableHead>Ações</TableHead>}
                       </TableRow>
                     </TableHeader>
                    <TableBody>
                       {relatorioData.map((dia, index) => (
                         <TableRow key={index}>
                           <TableCell>{formatYMDToBR(dia.date)}</TableCell>
                           <TableCell>{dia.entrada ? formatTime(dia.entrada) : '-'}</TableCell>
                           <TableCell>{dia.saida_almoco ? formatTime(dia.saida_almoco) : '-'}</TableCell>
                           <TableCell>{dia.volta_almoco ? formatTime(dia.volta_almoco) : '-'}</TableCell>
                           <TableCell>{dia.saida ? formatTime(dia.saida) : '-'}</TableCell>
                           <TableCell>{formatInterval(dia.total_horas)}</TableCell>
                           {canEditManually && (
                             <TableCell>
                               <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEditDay(dia.date)}
                                >
                                  Editar Dia
                                </Button>
                             </TableCell>
                           )}
                         </TableRow>
                       ))}
                    </TableBody>
                  </Table>

                  {/* Registros detalhados para edição (apenas para administração em ponto eletrônico) */}
                  {canApproveDirectly && timesheetRecords && timesheetRecords.length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-semibold text-lg mb-4">Registros Individuais - Edição Manual</h4>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Data/Hora</TableHead>
                            <TableHead>Tipo</TableHead>
                            <TableHead>Local</TableHead>
                            <TableHead>Observações</TableHead>
                            <TableHead>Ações</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {timesheetRecords
                            .filter(record => {
                              if (!startDate || !endDate) return true
                              const recordDate = new Date(record.record_date)
                              const start = new Date(startDate)
                              const end = new Date(endDate)
                              return recordDate >= start && recordDate <= end
                            })
                            .map((record) => (
                            <TableRow key={record.id}>
                              <TableCell>
                                {new Date(record.record_date).toLocaleDateString('pt-BR')} {formatTime(record.record_time)}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">
                                  {record.record_type === 'entrada' && 'Entrada'}
                                  {record.record_type === 'saida' && 'Saída'}
                                  {record.record_type === 'saida_almoco' && 'Saída Almoço'}
                                  {record.record_type === 'volta_almoco' && 'Volta Almoço'}
                                </Badge>
                              </TableCell>
                              <TableCell>{record.location || '-'}</TableCell>
                              <TableCell>{record.notes || '-'}</TableCell>
                              <TableCell>
                                <div className="flex space-x-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleEditRecord(record)}
                                  >
                                    <Edit className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDeleteRecord(record.id)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">Nenhum registro encontrado para este período</p>
                  <p className="text-sm text-gray-400 mt-2">
                    O funcionário não possui registros de ponto no mês atual
                  </p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal de Justificativa */}
        <Dialog open={justificativaModal} onOpenChange={setJustificativaModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {acao === "aprovar" || acao === "aprovar_final" ? "Aprovar" : "Reprovar"} Folha de Ponto
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  {folhaSelecionada?.funcionario} - {folhaSelecionada?.mes}
                </p>
                <Textarea
                  placeholder={
                    acao === "aprovar" || acao === "aprovar_final" 
                      ? "Justificativa da aprovação (opcional)" 
                      : "Justificativa da reprovação (obrigatório)"
                  }
                  value={justificativa}
                  onChange={(e) => setJustificativa(e.target.value)}
                  rows={4}
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setJustificativaModal(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={confirmarAcao}
                  className={acao === "aprovar" || acao === "aprovar_final" ? "bg-green-600 hover:bg-green-700" : ""}
                  variant={acao === "reprovar" || acao === "reprovar_admin" ? "destructive" : "default"}
                >
                  Confirmar {acao === "aprovar" || acao === "aprovar_final" ? "Aprovação" : "Reprovação"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal de Edição de Registro */}
        <EditTimesheetRecordModal
          isOpen={editRecordModal}
          onClose={() => {
            setEditRecordModal(false)
            setSelectedRecord(null)
          }}
          record={selectedRecord}
        />

        {/* Modal de Edição de Dia */}
        <EditDayTimesheetModal
          isOpen={isEditDayModalOpen}
          onClose={() => {
            setIsEditDayModalOpen(false)
            setSelectedDay(undefined)
          }}
          employeeId={selectedEmployeeId}
          employeeName={folhaSelecionada?.funcionario || ''}
          date={selectedDay?.date || ''}
          existingRecords={selectedDay?.records || []}
          onSuccess={() => incrementVersion()}
        />
      </div>
    </Layout>
  )
}

export default FolhasPonto
